import type { AccountStat } from '@kwenta/sdk/types'
import { formatNumber, truncateAddress } from '@kwenta/sdk/utils'
import type Wei from '@kwenta/wei'
import { useRouter } from 'next/router'
import { type FC, type MouseEvent, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { normalize } from 'viem/ens'
import { useEnsAvatar } from 'wagmi'

import DefaultProfileIcon from 'assets/svg/app/default-profile.svg'
import PointsIcon from 'assets/svg/app/points.svg'
import CopyCheckIcon from 'assets/svg/referrals/copy-check.svg'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { NO_VALUE } from 'constants/placeholder'
import ROUTES from 'constants/routes'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import { useAppSelector } from 'state/hooks'
import { selectTotalPoints } from 'state/points/selectors'
import media from 'styles/media'

const TraderProfileHeader: FC<AccountStat<Wei, number>> = memo(
	({ trader, traderShort, traderEns, rank, accountId }) => {
		const { t } = useTranslation()
		const router = useRouter()
		const points = useAppSelector(selectTotalPoints)
		const { copyToClipboard, CopyTooltip } = useCopyToClipboard({
			tooltipContent: 'Address Copied!',
			tooltipOffset: { x: 10, y: 10 },
		})
		const { data: ensAvatarIcon } = useEnsAvatar({
			name: normalize(traderEns ?? ''),
			chainId: 1,
		})

		const onClickTrader = useCallback(
			(trader: string) => {
				router.push(ROUTES.Leaderboard.Trader(trader))
			},
			[router]
		)

		const displayTraderAddress = useMemo(() => {
			if (traderShort) {
				return traderShort
			}
			if (trader) {
				return truncateAddress(trader)
			}
			return NO_VALUE
		}, [trader, traderShort])

		const userProfile = useMemo(
			() => [
				{
					label: t('leaderboard.profile.uid'),
					component: (
						<FlexDivRowCentered columnGap="5px">
							<StyledBody>{accountId ? truncateAddress(accountId) : NO_VALUE}</StyledBody>
							{accountId && (
								<StyledCopyCheckIcon
									onClick={(event: MouseEvent) => copyToClipboard(event, accountId.toString())}
								/>
							)}
						</FlexDivRowCentered>
					),
				},
				{
					label: t('leaderboard.profile.leaderboard'),
					component: (
						<ClickableStyledBody onClick={() => rank !== -1 && onClickTrader(trader)}>
							{rank === -1 ? NO_VALUE : `#${rank}`}
						</ClickableStyledBody>
					),
				},
				{
					label: t('leaderboard.profile.season-1-points'),
					component: (
						<FlexDivRowCentered columnGap="3px" justifyContent="flex-start">
							<Body color="preview" weight="semiBold">
								{formatNumber(points, { maxDecimals: 0 })}
							</Body>
							<PointsIcon />
						</FlexDivRowCentered>
					),
				},
			],
			[t, accountId, points, rank, copyToClipboard, onClickTrader, trader]
		)

		return (
			<ProfileContainer justifyContent="flex-start" columnGap="25px">
				<LabelContainer columnGap="15px">
					{ensAvatarIcon ? (
						<StyledProfileImage src={ensAvatarIcon} alt={(traderEns ?? '').toString()} />
					) : (
						<DefaultProfileIcon />
					)}
					<FlexDivCol rowGap="2px">
						{traderEns ? (
							<StyledBody>{traderEns}</StyledBody>
						) : (
							<StyledBody color="secondary">{t('leaderboard.profile.trader')}</StyledBody>
						)}
						<FlexDivRowCentered justifyContent="flex-start" columnGap="5px">
							<StyledBody color={traderEns ? 'secondary' : 'primary'}>
								{displayTraderAddress}
							</StyledBody>
							{displayTraderAddress !== NO_VALUE && (
								<StyledCopyCheckIcon
									onClick={(event: MouseEvent) => copyToClipboard(event, trader)}
								/>
							)}
						</FlexDivRowCentered>
					</FlexDivCol>
				</LabelContainer>
				<MetricsContainer columnGap="25px" justifyContent="flex-start">
					{userProfile.map(({ label, component }) => (
						<FlexDivCol key={label} rowGap="2px">
							<StyledBody color="secondary" size="small">
								{label}
							</StyledBody>
							{component && component}
						</FlexDivCol>
					))}
				</MetricsContainer>
				<CopyTooltip />
			</ProfileContainer>
		)
	}
)

const StyledBody = styled(Body)`
	${media.lessThan('sm')`
		font-size: 9px;
	`}
`

const ClickableStyledBody = styled(StyledBody)`
	cursor: pointer;
`

const StyledProfileImage = styled.img`
	border-radius: 50%;
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.text.tertiary};
	width: 35px;
	height: 35px;
`

const StyledCopyCheckIcon = styled(CopyCheckIcon)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
	cursor: pointer;
`

const LabelContainer = styled(FlexDivRowCentered)`
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
	padding-right: 25px;

	${media.lessThan('md')`
		column-gap: 10px;
		padding-right: 10px;
	`}
`

const MetricsContainer = styled(FlexDivRowCentered)`
	flex-wrap: wrap;
	row-gap: 15px;
`
const ProfileContainer = styled(FlexDivRowCentered)`
	${media.lessThan('md')`
		column-gap: 10px;
	`}
`

export default TraderProfileHeader
